<template>
  <div>
    <page-header :title="title" :container-class="containerClass" />
    <div class="page-section">
      <div :class="containerClass">
        <page-separator :title="'Enter emails to invite'" />
        <p class="invite-helper-text mb-3">One card per student. Each invitee can be assigned a program, tag, and course.</p>
        <b-form class="invite-form col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
          <div v-for="(stdEmail, index) in $v.emails.$each.$iter" :key="index" class="invite-student-row mb-3">
            <div class="invite-row-header">
              <strong>Student {{ Number(index) + 1 }}</strong>
              <a
                href="#"
                class="invite-delete-btn"
                @click.prevent="removeEmail(index)"
                v-if="emails.length > 1"
                aria-label="Remove student"
              >
                <md-icon>delete_outline</md-icon>
              </a>
            </div>

            <div class="invite-row-grid">
              <b-form-group :label="'Email'" label-for="email" label-class="form-label" class="invite-field mb-0">
                <b-form-input
                  :id="`email-${index}`"
                  placeholder="student@example.com"
                  v-model.trim="stdEmail.email.$model"
                  :state="(!stdEmail.email.required || !stdEmail.email.email) && stdEmail.$dirty ? false : null"
                />

                <b-form-invalid-feedback v-if="!stdEmail.email.email">
                  Invalid email. Please make sure that it's in correct format i.e.
                  test@example.com.</b-form-invalid-feedback
                >
                <b-form-invalid-feedback v-else>This field is required.</b-form-invalid-feedback>
              </b-form-group>

              <b-form-group label="Program" label-for="enrolled_program" label-class="form-label" class="invite-field mb-0">
                <v-select
                  :id="`enrolled-program-${index}`"
                  class="form-control v-select-custom"
                  label="title"
                  v-model="stdEmail.enrolled_program.$model"
                  :reduce="program => program.id"
                  placeholder="Select program..."
                  :options="allPrograms"
                  :loading="areProgramsLoading"
                  @input="program => onProgramChange(index, program)"
                >
                  <template slot="option" slot-scope="option">
                    <span>{{ option.title }}</span>
                  </template>
                </v-select>
              </b-form-group>

              <b-form-group label="Tag" label-for="selected_tag" label-class="form-label" class="invite-field mb-0">
                <v-select
                  :id="`selected-tag-${index}`"
                  class="form-control v-select-custom"
                  label="title"
                  v-model="stdEmail.selected_tag.$model"
                  :reduce="tag => tag.id"
                  :placeholder="stdEmail.enrolled_program.$model ? 'Select tag...' : 'Select program first'"
                  :options="getTagOptions(stdEmail.enrolled_program.$model)"
                  :loading="isProgramTagsLoading(stdEmail.enrolled_program.$model)"
                  :disabled="!stdEmail.enrolled_program.$model"
                >
                  <template slot="option" slot-scope="option">
                    <span>{{ option.title }}</span>
                  </template>
                </v-select>
              </b-form-group>

              <b-form-group label="Course" label-for="assigned_course" label-class="form-label" class="invite-field mb-0">
                <v-select
                  :id="`assigned-course-${index}`"
                  class="form-control v-select-custom"
                  label="title"
                  v-model="stdEmail.assigned_course.$model"
                  :reduce="course => course.id"
                  :placeholder="stdEmail.enrolled_program.$model ? 'Select course...' : 'Select program first'"
                  :options="getCourseOptions(stdEmail.enrolled_program.$model)"
                  :loading="isProgramCoursesLoading(stdEmail.enrolled_program.$model)"
                  :disabled="!stdEmail.enrolled_program.$model"
                >
                  <template slot="option" slot-scope="option">
                    <span>{{ option.title }}</span>
                  </template>
                </v-select>
              </b-form-group>

              <div class="invite-row-actions">
                <a
                  href="#"
                  class="invite-delete-btn invite-delete-btn-desktop"
                  @click.prevent="removeEmail(index)"
                  v-if="emails.length > 1"
                  aria-label="Remove student"
                >
                  <md-icon>delete_outline</md-icon>
                </a>
              </div>
            </div>
          </div>

          <b-btn variant="outline-secondary" class="invite-add-btn btn-normal mb-4" @click.prevent="addEmail()">
            <md-icon>add</md-icon>
            Add another student
          </b-btn>

          <div class="invite-submit-row">
            <b-btn variant="primary" style="width: 170px" type="submit" class="btn-normal">
              <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
              <span v-else>Invite Students</span>
            </b-btn>
            <span class="invite-ready-count">{{ readyInviteCount }} of {{ emails.length }} row(s) ready to send</span>
          </div>
        </b-form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { get } from 'lodash';
import { required, email } from 'vuelidate/lib/validators';
import Page from '../../../components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { USER_ROLE_TYPES, USER_ROLES, SEND_INVITE_STATUSES } from '../../../common/constants';
export default {
  name: 'PostInvite',
  components: { PageHeader, PageSeparator },
  extends: Page,
  data() {
    return {
      title: 'Invite Students',
      emails: [
        {
          email: '',
          role_type: USER_ROLE_TYPES.STUDENT,
          role: USER_ROLES.STUDENT,
          selected_tag: null,
          enrolled_program: null,
          assigned_course: null,
        },
      ],
      isLoading: false,
      areProgramsLoading: false,
      coursesLoadingByProgram: {},
      tagsLoadingByProgram: {},
      allPrograms: [],
      coursesByProgram: {},
      tagsByProgram: {},
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    readyInviteCount() {
      return this.emails.filter(row => email(row.email)).length;
    },
    breadcrumb() {
      return [
        { text: 'Home', to: this.routes.home },
        { text: 'Manage Invites', to: { name: 'manage-student-invite-list' } },
        {
          text: 'Invite Students',
          active: true,
        },
      ];
    },
  },
  validations() {
    return {
      emails: {
        required,
        $each: {
          email: { required, email },
          role_type: { required },
          role: { required },
          selected_tag: {},
          enrolled_program: {},
          assigned_course: {},
        },
      },
    };
  },

  methods: {
    ...mapActions('invite', ['createInvite']),
    ...mapActions('program', ['getPublicSchoolPrograms']),
    ...mapActions('attendance', ['getTags']),
    ...mapActions('courses', ['getProgramCourses']),

    getDefaultInviteRow() {
      return {
        email: '',
        role_type: USER_ROLE_TYPES.STUDENT,
        role: USER_ROLES.STUDENT,
        selected_tag: null,
        enrolled_program: null,
        assigned_course: null,
      };
    },

    addEmail(index = this.emails.length - 1) {
      this.emails.splice(index + 1, 0, this.getDefaultInviteRow());
    },

    removeEmail(index) {
      this.emails.splice(index, 1);
    },

    getCourseOptions(programId) {
      return programId ? this.coursesByProgram[programId] || [] : [];
    },

    getTagOptions(programId) {
      return programId ? this.tagsByProgram[programId] || [] : [];
    },

    isProgramCoursesLoading(programId) {
      return !!(programId && this.coursesLoadingByProgram[programId]);
    },

    isProgramTagsLoading(programId) {
      return !!(programId && this.tagsLoadingByProgram[programId]);
    },

    async onProgramChange(index, programId) {
      this.emails[index].selected_tag = null;
      this.emails[index].assigned_course = null;
      if (programId) {
        await Promise.all([this.fetchProgramCourses(programId), this.fetchProgramTags(programId)]);
      }
    },

    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const response = await this.getPublicSchoolPrograms({ school: get(this.getLoggedInUser, 'linked_entity.id') });
        this.allPrograms = response.data;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },

    normalizeTagResponse(response) {
      return get(response, 'data.data.results') || get(response, 'data.data') || get(response, 'data') || [];
    },

    async fetchProgramTags(programId) {
      if (this.tagsByProgram[programId] || this.tagsLoadingByProgram[programId]) {
        return;
      }

      this.$set(this.tagsLoadingByProgram, programId, true);
      try {
        const response = await this.getTags({
          program__id: programId,
          limit: 15,
          offset: 0,
        });
        this.$set(this.tagsByProgram, programId, this.normalizeTagResponse(response));
      } catch (err) {
        this.$set(this.tagsByProgram, programId, []);
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.$set(this.tagsLoadingByProgram, programId, false);
    },

    async fetchProgramCourses(programId) {
      if (this.coursesByProgram[programId] || this.coursesLoadingByProgram[programId]) {
        return;
      }

      this.$set(this.coursesLoadingByProgram, programId, true);
      try {
        const response = await this.getProgramCourses({
          program: programId,
          school: get(this.getLoggedInUser, 'linked_entity.id'),
        });
        this.$set(this.coursesByProgram, programId, response.data || []);
      } catch (err) {
        this.$set(this.coursesByProgram, programId, []);
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.$set(this.coursesLoadingByProgram, programId, false);
    },

    async onSubmit() {
      this.isLoading = true;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          await this.createInvite({
            invites: this.emails,
          });
          this.makeToast({ variant: 'success', msg: 'Invites sent successfully' });
          setTimeout(() => this.$router.push({ name: 'manage-student-invite-list' }), 250);
        } catch (err) {
          if (get(err.response.data, 'data.invite_status') === SEND_INVITE_STATUSES.NOT_CREATED) {
            this.makeToast({ variant: 'danger', msg: "Couldn't send invites to the given email address(es)" });
          } else {
            this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
          }
        }
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields correctly.' });
      }

      this.isLoading = false;
    },
  },

  mounted() {
    this.fetchPrograms();
  },
};
</script>

<style scoped>
.invite-helper-text {
  color: #6c757d;
}

.invite-form {
  max-width: 1100px;
}

.invite-student-row {
  padding: 1rem;
  border: 1px solid #dfe3e7;
  border-radius: 0.5rem;
  background: #fff;
}

.invite-row-header {
  display: none;
}

.invite-row-grid {
  display: grid;
  grid-template-columns: minmax(220px, 1.35fr) minmax(190px, 1fr) minmax(170px, 0.9fr) minmax(190px, 1fr) 44px;
  gap: 0.75rem;
  align-items: flex-start;
}

.invite-field ::v-deep .form-label,
.invite-field ::v-deep legend {
  margin-bottom: 0.35rem;
  font-weight: 600;
}

.invite-row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 2rem;
  white-space: nowrap;
}

.invite-delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: #dc3545;
  border: 1px solid #dfe3e7;
  border-radius: 0.4rem;
}

.invite-add-btn {
  min-width: 220px;
}

.invite-submit-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.invite-ready-count {
  color: #6c757d;
  font-weight: 600;
}

@media (max-width: 767.98px) {
  .invite-form {
    max-width: 420px;
    margin: 0 auto;
  }

  .invite-student-row {
    padding: 0.9rem;
  }

  .invite-row-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.85rem;
  }

  .invite-row-grid {
    display: block;
  }

  .invite-field {
    margin-bottom: 0.85rem !important;
  }

  .invite-row-actions,
  .invite-delete-btn-desktop {
    display: none;
  }

  .invite-add-btn,
  .invite-submit-row .btn {
    width: 100%;
  }

  .invite-submit-row {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
}
</style>
